import clsx from 'clsx';
import { ChangeEvent, DragEvent as ReactDragEvent, FC, useMemo } from 'react';
import { IonIcon } from '@ionic/react';
import { reorderThreeOutline, settingsOutline } from 'ionicons/icons';
import Button from '../../../components/Button/Button';
import Checkbox from '../../../components/Checkbox/Checkbox';
import Input from '../../../components/Input/Input';
import Combobox from '../../../components/Combobox/Combobox';
import { useTranslate } from '../../../hooks/context/useTranslate';
import {
  ControlItemValue,
  FilterError,
  StaticControlItem,
} from '../../../types/types';
import { ExportColumn, ExportColumnOptions } from '../types';
import { isNumberExportColumn } from '../utils/isNumberExportColumn';
import { isStringExportColumn } from '../utils/isStringExportColumn';
import resolveDecimalPlacesError from '../utils/resolveDecimalPlacesError';
import resolveMaxLengthError from '../utils/resolveMaxLengthError';

interface ExportColumnItemProps {
  column: ExportColumn;
  isDragging: boolean;
  isSettingsOpen: boolean;
  isSectionEnabled: boolean;
  onToggleColumn: (columnName: string, selected: boolean) => void;
  onOpenColumnSettings: (columnName: string) => void;
  onColumnTextOptionChange: (
    columnName: string,
    optionName: keyof Pick<
      ExportColumnOptions,
      'prefix' | 'suffix' | 'maxLength' | 'decimalPlaces'
    >
  ) => (event: ChangeEvent<HTMLInputElement>) => void;
  onRoundingModeChange: (
    columnName: string
  ) => (value: ControlItemValue) => void;
  onDragStart: (
    event: ReactDragEvent<HTMLDivElement>,
    columnName: string
  ) => void;
  onDragOver: (event: ReactDragEvent<HTMLDivElement>) => void;
  onDrop: (event: ReactDragEvent<HTMLDivElement>, columnName: string) => void;
  onDragEnd: () => void;
}

const ExportColumnItem: FC<ExportColumnItemProps> = ({
  column,
  isDragging,
  isSettingsOpen,
  isSectionEnabled,
  onToggleColumn,
  onOpenColumnSettings,
  onColumnTextOptionChange,
  onRoundingModeChange,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) => {
  const { t } = useTranslate();
  const isNumber = isNumberExportColumn(column);
  const isString = isStringExportColumn(column);

  const isRoundingDisabled = column.exportOptions.roundingMode === 'none';

  const decimalPlacesError = useMemo<FilterError>(
    () =>
      isNumber
        ? resolveDecimalPlacesError(
            column.exportOptions.decimalPlaces,
            column.exportOptions.roundingMode,
            t
          )
        : null,
    [
      isNumber,
      column.exportOptions.decimalPlaces,
      column.exportOptions.roundingMode,
      t,
    ]
  );

  const maxLengthError = useMemo<FilterError>(
    () =>
      isString
        ? resolveMaxLengthError(column.exportOptions.maxLength, t)
        : null,
    [isString, column.exportOptions.maxLength, t]
  );

  const columnOptionsError = decimalPlacesError ?? maxLengthError;

  const roundingOptions = useMemo<StaticControlItem[]>(
    () => [
      { label: t('export.roundingNone'), value: 'none' },
      { label: t('export.roundingUp'), value: 'up' },
      { label: t('export.roundingDown'), value: 'down' },
      { label: t('export.roundingMath'), value: 'math' },
    ],
    [t]
  );

  return (
    <div
      className={clsx('dataset-export-column', isDragging && 'dragging')}
      onDragOver={onDragOver}
      onDrop={(event) => onDrop(event, column.name)}
    >
      <div
        className="dataset-export-column-main"
        draggable
        onDragStart={(event) => onDragStart(event, column.name)}
        onDragEnd={onDragEnd}
        title={t('export.dragColumn')}
      >
        <IonIcon
          icon={reorderThreeOutline}
          className="dataset-export-drag-icon"
        />
        <Checkbox
          value={column.selected}
          onChange={(value) => onToggleColumn(column.name, value)}
          label={column.label}
          disabled={!isSectionEnabled}
        />
        <Button
          className={clsx(
            'dataset-export-column-settings',
            isSettingsOpen && 'active',
            columnOptionsError && 'invalid'
          )}
          fill="clear"
          round={false}
          title={
            columnOptionsError
              ? columnOptionsError[0]
              : t('export.columnSettings')
          }
          onClick={() => onOpenColumnSettings(column.name)}
          disabled={!isSectionEnabled}
        >
          <IonIcon icon={settingsOutline} />
        </Button>
      </div>

      {isSettingsOpen && (
        <div className="dataset-export-column-settings-panel">
          {/* Inline settings keep field-specific options tied to the selected export column. */}
          <Input
            label={t('export.prefix')}
            type="text"
            value={column.exportOptions.prefix}
            onChange={(_, event) =>
              onColumnTextOptionChange(column.name, 'prefix')(event)
            }
          />
          <Input
            label={t('export.suffix')}
            type="text"
            value={column.exportOptions.suffix}
            onChange={(_, event) =>
              onColumnTextOptionChange(column.name, 'suffix')(event)
            }
          />

          {isNumber && (
            <>
              <Combobox
                label={t('export.rounding')}
                name="export-rounding"
                value={column.exportOptions.roundingMode}
                onChange={onRoundingModeChange(column.name)}
                options={roundingOptions}
              />
              <Input
                label={t('export.decimalPlaces')}
                name={`export-decimal-places-${column.name}`}
                type="text"
                inputMode="numeric"
                error={decimalPlacesError}
                disabled={isRoundingDisabled}
                value={column.exportOptions.decimalPlaces}
                onChange={(_, event) =>
                  onColumnTextOptionChange(column.name, 'decimalPlaces')(event)
                }
              />
            </>
          )}

          {isString && (
            <Input
              label={t('export.maxLength')}
              name={`export-max-length-${column.name}`}
              type="text"
              inputMode="numeric"
              error={maxLengthError}
              value={column.exportOptions.maxLength}
              onChange={(_, event) =>
                onColumnTextOptionChange(column.name, 'maxLength')(event)
              }
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ExportColumnItem;
